import React from "react";
import _ from "lodash";
import { AnyAction } from "redux";

import { isFailure, Protection, SafeSearchService } from "@sportal/api";
import { ModalService } from "../../components/modal";
import { SBThunkAction } from "../redux.types";
import { getChangedFields } from "./profiles.helpers";
import {
  ensureProtection,
  saveCustomProtection,
  wipeCustomProtection,
} from "../../pages/profiles/components/webFilters/webFilters.actions";
import { getValidationErrors } from "../../pages/profiles/components/schedule/schedules.selectors";
import { saveSchedules } from "../../pages/profiles/components/schedule/schedules.actions";
import { saveSsProfile } from "../../pages/profiles/components/internetSecurity/subscriberSafety/ssProfiles.actions";
import {
  CHANGE_PROTECTION,
  ChangeProtectionAction,
  CHANGE_SAFE_SEARCH_SERVICE,
  ChangeSafeSearchServiceAction,
  RENAME_PROFILE_SUCCESS,
  RenameProfileSuccessAction,
  RENAME_PROFILE_FAILURE,
  RenameProfileFailureAction,
  SAVE_PROFILE_SUCCESS,
  SaveProfileSuccessAction,
  SAVE_PROFILE_FAILURE,
  SaveProfileFailureAction,
  ProfileError,
  SBProfile,
} from "./profiles.types";
import {
  Notificator,
  NotificationActionTypes,
} from "../../components/notification";
import ProfileDialog from "../../pages/profiles/components/profileDialog/ProfileDialog";
import {
  getProfilesChangedList,
  getSavedProfilesMap,
  shouldSaveChangesToBackEnd,
} from "./profiles.selectors";
import { getSubscriberId } from "../info/info.selectors";
import { getProfiles, getSchedules } from "../root.selectors";

export const renameProfileSuccess = (
  profile: string,
  newName: string,
  currentName: string
): RenameProfileSuccessAction => ({
  type: RENAME_PROFILE_SUCCESS,
  payload: { profile, newName, currentName },
});

export const renameProfileFailure = (
  error: ProfileError
): RenameProfileFailureAction => ({
  type: RENAME_PROFILE_FAILURE,
  payload: error,
});

export const openEditProfileModal =
  (profileNames: string[], profile: SBProfile): SBThunkAction<void, any> =>
  (dispatch, getState) => {
    const shouldSendRequest = shouldSaveChangesToBackEnd(getState());

    ModalService.show((modal) => ({
      dialog: (
        <ProfileDialog
          profiles={profileNames}
          profile={profile}
          modal={modal}
          title={"edit_group"}
          submitText={"rename"}
        />
      ),
    }))
      .then((name) => {
        if (shouldSendRequest) {
          dispatch(renameProfileRequest(profile.id, name));
        } else {
          dispatch(renameProfileSuccess(profile.id, name, profile.name));
        }
      })
      .catch(() => null);
  };

// TODO: rename `renameProfileRequest` and `renameProfile` (or replace field-changing actions with generic one)
export const renameProfileRequest =
  (
    profileId: string,
    newName: string
  ): SBThunkAction<
    Promise<void>,
    | RenameProfileSuccessAction
    | SaveProfileSuccessAction
    | RenameProfileFailureAction
    | NotificationActionTypes
  > =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const subscriberId = getSubscriberId(state);
    const profiles = getSavedProfilesMap(state);
    const currentName = profiles[profileId].name;

    const result = await api.ssm.profiles.update(subscriberId, currentName, {
      name: newName,
    });

    if (isFailure(result)) {
      dispatch(Notificator.error("failed_to_save_profile"));
      dispatch(renameProfileFailure(result.error));
      return Promise.reject();
    }

    dispatch(renameProfileSuccess(profileId, newName, currentName));
    dispatch(saveProfileSuccess());
  };

export const changeProtection = (
  profile: string,
  protection: Protection
): ChangeProtectionAction => ({
  type: CHANGE_PROTECTION,
  payload: { profile, protection },
});

export const changeSafeSearchService = (
  profile: string,
  service: SafeSearchService,
  enabled: boolean
): ChangeSafeSearchServiceAction => ({
  type: CHANGE_SAFE_SEARCH_SERVICE,
  payload: { profile, service, enabled },
});

export const ERRORS = {
  policies: "policies_save_request_fail",
  schedulesHomework: "schedules_homework_save_request_fail",
  schedulesInternetOff: "schedules_internetOff_save_request_fail",
  ss: "ss_save_request_fail",
  protection: "cannot_save_protection_level",
  safeSearch: "cannot_save_safe_search",
};

// TODO: probably can be named as update? saveProfileChanges?
// TODO: add action types to union once we get them
export const saveProfile =
  (
    profileId: string
  ): SBThunkAction<
    Promise<void>,
    SaveProfileSuccessAction | SaveProfileFailureAction | AnyAction
  > =>
  async (dispatch, getState, { api }) => {
    dispatch(ensureProtection(getProfilesChangedList(getState())[profileId]));

    const state = getState();
    const subscriberId = getSubscriberId(state);
    const profiles = getProfiles(state);
    const schedules = getSchedules(state);
    const profile = getProfilesChangedList(state)[profileId];

    const updates = getChangedFields(profileId, profiles);
    const saveErrors = [];

    const errors = getValidationErrors(schedules);
    if (!_.isEmpty(errors)) {
      return Promise.reject(errors);
    }

    if (!_.isEmpty(updates)) {
      const result = await api.ssm.profiles.update(
        subscriberId,
        profile.name,
        updates
      );

      if (isFailure(result)) {
        _.has(updates, "protection") && saveErrors.push(ERRORS.protection);
        _.has(updates, "safe-search-services") &&
          saveErrors.push(ERRORS.safeSearch);

        dispatch(saveProfileFailure(result.error));
      }
    }

    let saveProfile = Promise.resolve();
    if (profile.protection === Protection.Custom) {
      saveProfile = saveProfile
        .then(() => dispatch(saveCustomProtection(profile)))
        .catch(() => {
          saveErrors.push(ERRORS.policies);
        });
    } else {
      dispatch(wipeCustomProtection(profile.id));
    }

    saveProfile = saveProfile
      .then(() => dispatch(saveSchedules()))
      .catch(({ scheduleName }) => {
        const error =
          scheduleName === "internet-off0"
            ? ERRORS.schedulesInternetOff
            : ERRORS.schedulesHomework;
        saveErrors.push(error);
      });

    saveProfile = saveProfile
      .then(() => dispatch(saveSsProfile(profileId)))
      .catch(() => {
        saveErrors.push(ERRORS.ss);
      });

    return saveProfile
      .then(() =>
        !_.isEmpty(saveErrors)
          ? Promise.reject(
              _.map(saveErrors, (error) => ({
                type: "save",
                message: error,
              }))
            )
          : Promise.resolve()
      )
      .then(() => {
        dispatch(saveProfileSuccess());
      });
  };

export const saveProfileSuccess = (): SaveProfileSuccessAction => ({
  type: SAVE_PROFILE_SUCCESS,
});

export const saveProfileFailure = (
  error: ProfileError
): SaveProfileFailureAction => ({
  type: SAVE_PROFILE_FAILURE,
  payload: error,
});
